<template>
  <div id="listWidget" class="mb-5">
    <ul class="breadcrumb">
      <li class="breadcrumb-item">{{ group?.name }}</li>
      <li class="breadcrumb-item active">조명 버튼 관리</li>
    </ul>

    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <p class="mb-2" style="font-size: 0.85rem; line-height: 1.6; color: #495057;">
          물리 버튼(최대 12포트)마다 연결할 조명을 선택·등록합니다.
          같은 제어기 채널은 네트워크 없이 동작하고, 다른 제어기 조명은 연결이 필요합니다.
          설정 모드에서 벽면 버튼을 누르면 아래에 등록 대기 항목이 표시됩니다.
        </p>
        <p class="mb-0" style="font-size: 0.85rem; color: #6c757d;">
          등록된 버튼 {{ registeredButtons.length }} / 12
        </p>
      </div>
    </div>

    <div class="section-card card-item mb-3" v-if="pendingSettingDevices.length > 0">
      <div class="section-header">
        <i class="fa-solid fa-plug me-2 text-primary"></i>
        <span class="section-title">등록 대기 (설정 모드)</span>
      </div>
      <div class="section-body">
        <div
          v-for="(item, index) in pendingSettingDevices"
          :key="`${item.serial}-${item.data?.port}-${index}`"
          class="button-list-row"
          @click="onPendingClick(item)"
        >
          <div>
            <div class="button-list-name">포트 {{ toDisplayLampPort(item.data?.port ?? 0, item.serial) }}</div>
            <div class="button-list-sub">{{ item.serial }}</div>
          </div>
          <i class="fa-solid fa-chevron-right text-muted"></i>
        </div>
      </div>
    </div>

    <div class="section-card card-item mb-3">
      <div class="section-header">
        <i class="fa-solid fa-toggle-on me-2 text-primary"></i>
        <span class="section-title">등록된 버튼</span>
      </div>
      <div class="section-body">
        <div v-if="registeredButtons.length === 0" class="text-muted" style="font-size: 0.85rem;">
          등록된 버튼이 없습니다.
        </div>
        <div
          v-for="item in registeredButtons"
          :key="item._id"
          class="button-list-row"
          @click="onEditClick(item._id)"
        >
          <div>
            <div class="button-list-name">{{ item.name }}</div>
            <div class="button-list-sub">
              연결포트 {{ formatPorts(item) }} · 매핑 조명 {{ mappingInfo[item._id]?.count ?? 0 }}개<span
                v-if="mappingInfo[item._id]?.portsLabel"
              > (포트 {{ mappingInfo[item._id].portsLabel }})</span>
            </div>
          </div>
          <i class="fa-solid fa-chevron-right text-muted"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
} from 'vue';
import Axios, { GroupApi, VirtualDeviceApi } from '@/api';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { formatDisplayLampPorts, toDisplayLampPort } from '@/common/utils/controllerDisplayName';

const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();
const group = ref(undefined);
const mappingInfo = ref({});

const settingModeDevices = computed(() => store.getters['home/getSettingModeDevices']);

const registeredButtons = computed(() => {
  return (group.value?.devices || []).filter((d) => d.type === 1 && !d.isVirtual);
});

const pendingSettingDevices = computed(() => {
  const registeredPortKeys = new Set(
    registeredButtons.value.map((d) => `${d.controllerId}:${(d.item?.ports || [])[0]}`),
  );
  return (settingModeDevices.value || []).filter((d) => {
    if (d.type !== 1 || d.data?.port === undefined) return false;
    return !registeredPortKeys.has(`${d.serial}:${d.data.port}`);
  });
});

const formatPorts = (device) => {
  const ports = device?.item?.ports || [];
  return formatDisplayLampPorts(ports, device?.controllerId);
};

const extractMappedPortsLabel = (items, buttonControllerId) => {
  const labels = [];
  const seen = new Set();
  for (const it of items || []) {
    if (!it) continue;
    const ports = Array.isArray(it.item?.ports)
      ? it.item.ports
      : it.item?.ports != null
        ? [it.item.ports]
        : [];
    if (ports.length === 0) continue;
    const display = formatDisplayLampPorts(ports, it.controllerId);
    if (!display) continue;
    const crossController =
      buttonControllerId && it.controllerId && String(it.controllerId) !== String(buttonControllerId);
    const label = crossController
      ? `${String(it.controllerId).split('_').pop() || it.controllerId}:${display}`
      : display;
    if (seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels.join(', ');
};

onBeforeMount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '조명 버튼 관리',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
    isShowRightHome: true,
  });
});

onBeforeUnmount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
    isShowRightHome: false,
  });
});

onMounted(async () => {
  await requestGroup();
  await loadMappingInfo();
});

const requestGroup = async () => {
  try {
    const result = await Axios.get(GroupApi.GROUP_INFO.replace(':id', route.params.groupId));
    if (result.resultCode === '0000') {
      group.value = result.resultData;
      return;
    }
    emitter.emit('showPopAlert', {
      description: result.resultMessage,
      okCallback: () => router.go(-1),
    });
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: '그룹 정보 조회에 실패했습니다.',
      okCallback: () => router.go(-1),
    });
  }
};

const loadMappingInfo = async () => {
  const info = {};
  await Promise.all(
    registeredButtons.value.map(async (btn) => {
      try {
        const result = await Axios.get(VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', btn._id));
        if (result.resultCode === '0000' && result.resultData?.mappingType === 1) {
          const raw = result.resultData.items;
          const items = Array.isArray(raw) ? raw : Object.values(raw || {});
          info[btn._id] = {
            count: items.length,
            portsLabel: extractMappedPortsLabel(items, btn.controllerId),
          };
        } else {
          info[btn._id] = { count: 0, portsLabel: '' };
        }
      } catch {
        info[btn._id] = { count: 0, portsLabel: '' };
      }
    }),
  );
  mappingInfo.value = info;
};

const onPendingClick = (item) => {
  if (registeredButtons.value.length >= 12) {
    emitter.emit('showPopAlert', {
      description: '물리 버튼은 최대 12개까지 등록할 수 있습니다.',
    });
    return;
  }
  router.push({
    path: `/settings/light/groups/${route.params.groupId}/buttons/new`,
    query: { port: item.data.port, serial: item.serial },
  });
};

const onEditClick = (deviceId) => {
  router.push(`/settings/light/groups/${route.params.groupId}/buttons/${deviceId}/edit`);
};
</script>

<style scoped>
.instruction-card,
.section-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header,
.section-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title,
.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body,
.section-body {
  padding: 0.75rem 1rem;
}

.button-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
}

.button-list-row:last-child {
  border-bottom: none;
}

.button-list-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #212529;
}

.button-list-sub {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.15rem;
}
</style>
